import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { BUSINESS } from '../data/businessInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ count = 0 }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < count ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
    ))}
  </div>
);

const initials = (name) =>
  (name || 'G').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

// Live Google Business reviews. Renders nothing until the backend is configured with an API key + Place ID.
const GoogleReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/reviews/google`)
      .then((res) => { if (mounted) setData(res.data); })
      .catch(() => { if (mounted) setData({ configured: false, reviews: [] }); });
    return () => { mounted = false; };
  }, []);

  if (!data || !data.configured) return null;
  const reviews = (data.reviews || []).filter((r) => r.text);
  if (reviews.length === 0) return null;

  // aggregateRating + review structured data (only present when live reviews are configured)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HomeAndConstructionBusiness',
    '@id': `${BUSINESS.url}/#business`,
    name: BUSINESS.name,
    url: BUSINESS.url,
    telephone: BUSINESS.telephone,
    image: BUSINESS.image,
    address: {
      '@type': 'PostalAddress',
      addressLocality: BUSINESS.addressLocality,
      addressRegion: BUSINESS.addressRegion,
      addressCountry: BUSINESS.addressCountry,
    },
    ...(data.rating != null && data.user_rating_count > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: Number(data.rating).toFixed(1),
            reviewCount: data.user_rating_count,
            bestRating: '5',
            worstRating: '1',
          },
        }
      : {}),
    review: reviews.slice(0, 6).map((r) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.author_name || 'Google user' },
      reviewRating: { '@type': 'Rating', ratingValue: String(r.rating || 5), bestRating: '5', worstRating: '1' },
      reviewBody: r.text,
    })),
  };

  return (
    <section id="google-reviews" className="py-20 bg-white" data-testid="google-reviews-section">
      <script type="application/ld+json" data-reviews-jsonld="true" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-100 mb-4">Google Reviews</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Rated by Our Neighbors</h2>
          <div className="flex items-center justify-center gap-3">
            {data.rating != null && (
              <>
                <span className="text-3xl font-bold text-slate-900">{Number(data.rating).toFixed(1)}</span>
                <Stars count={Math.round(data.rating)} />
              </>
            )}
            {data.user_rating_count > 0 && (
              <span className="text-slate-600">from {data.user_rating_count} Google reviews</span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.slice(0, 6).map((r, i) => (
            <Card key={i} className="border-slate-200 hover:shadow-lg transition-shadow" data-testid={`google-review-${i}`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Avatar className="w-11 h-11">
                    {r.author_photo_url && <AvatarImage src={r.author_photo_url} alt={r.author_name} referrerPolicy="no-referrer" />}
                    <AvatarFallback className="bg-gradient-to-br from-brand-blue to-brand-teal text-white font-semibold">
                      {initials(r.author_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{r.author_name || 'Google user'}</div>
                    <div className="text-xs text-slate-500">{r.relative_time}</div>
                  </div>
                  <svg className="w-5 h-5 ml-auto flex-shrink-0" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                </div>
                <Stars count={r.rating || 0} />
                <p className="text-slate-700 mt-3 text-sm leading-relaxed line-clamp-6">{r.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {data.google_maps_uri && (
          <div className="text-center mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button asChild className="bg-gradient-to-r from-brand-blue to-brand-teal text-white" data-testid="leave-review-btn">
              <a href="https://maps.app.goo.gl/VAC2QCSLS7qxmZEA8?g_st=ac" target="_blank" rel="noopener noreferrer">
                <Star className="w-4 h-4 mr-2 fill-white" /> Leave Us a Review
              </a>
            </Button>
            <Button asChild variant="outline" className="border-slate-300" data-testid="google-reviews-view-all">
              <a href={data.google_maps_uri} target="_blank" rel="noopener noreferrer">
                See all reviews on Google <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default GoogleReviews;
